import { useState, useRef } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import { CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Popover, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import 'react-datepicker/dist/react-datepicker.css'

// Register Chinese Traditional locale
import { zhTW } from 'date-fns/locale'
registerLocale('zh-TW', zhTW)

interface DatePickerProps {
  value?: Date | null
  onChange: (date: Date | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

const DatePicker = ({ 
  value, 
  onChange, 
  placeholder = "設定到期時間", 
  disabled = false,
  className = ""
}: DatePickerProps) => {
  const datePickerRef = useRef<ReactDatePicker>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const [panelPosition, setPanelPosition] = useState({ top: true, left: true })

  // 計算最佳位置
  const calculatePosition = () => {
    if (!buttonRef.current) return
    
    const rect = buttonRef.current.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const panelHeight = 350 // 估計面板高度
    const panelWidth = 300 // 估計面板寬度
    
    // 判斷是否有足夠空間在下方顯示
    const hasSpaceBelow = rect.bottom + panelHeight < windowHeight
    // 判斷是否有足夠空間在右側顯示
    const hasSpaceRight = rect.left + panelWidth < windowWidth
    
    setPanelPosition({
      top: hasSpaceBelow,
      left: hasSpaceRight
    })
  }

  const handleDateChange = (date: Date | null, close: () => void) => {
    onChange(date)
    close()
  }

  const handleClearDate = (e: React.MouseEvent, close: () => void) => {
    e.stopPropagation()
    onChange(null)
    close()
  }

  const formatDate = (date: Date | null) => {
    if (!date) return placeholder
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const isOverdue = value && value < new Date(new Date().setHours(0, 0, 0, 0))
  const isDueToday = value && value.toDateString() === new Date().toDateString()
  const isDueSoon = value && !isOverdue && !isDueToday && value <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) // 3天內

  return (
    <Popover className="relative inline-block text-left w-full">
      {({ close }) => (
        <>
          <div ref={buttonRef} className="relative">
            <Popover.Button
              as="div"
              disabled={disabled}
              onClick={calculatePosition}
              className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-md border transition-colors duration-150 cursor-pointer ${
                disabled 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white hover:bg-gray-50'
              } ${
                isOverdue 
                  ? 'border-red-300 text-red-600' 
                  : isDueToday 
                    ? 'border-yellow-300 text-yellow-600'
                    : isDueSoon
                      ? 'border-orange-300 text-orange-600'
                      : 'border-gray-300 text-gray-700'
              } ${className}`}
            >
              <CalendarDaysIcon className={`w-4 h-4 ${
                isOverdue 
                  ? 'text-red-500' 
                  : isDueToday 
                    ? 'text-yellow-500'
                    : isDueSoon
                      ? 'text-orange-500'
                      : 'text-gray-400'
              }`} />
              <span className="truncate flex-1">{formatDate(value || null)}</span>
            </Popover.Button>
            {value && !disabled && (
              <button
                onClick={(e) => handleClearDate(e, close)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 transition-colors duration-150 z-10"
                title="清除到期時間"
              >
                <XMarkIcon className="w-3 h-3 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Popover.Panel
              className={`absolute bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
                panelPosition.top ? 'top-full mt-2' : 'bottom-full mb-2'
              } ${
                panelPosition.left ? 'left-0' : 'right-0'
              }`}
              style={{
                zIndex: 50
              }}
            >
              <div className="p-2">
                <ReactDatePicker
                  ref={datePickerRef}
                  selected={value}
                  onChange={(date) => handleDateChange(date as Date, close)}
                  inline
                  locale="zh-TW"
                  dateFormat="yyyy/MM/dd"
                  todayButton="今天"
                  placeholderText={placeholder}
                  minDate={new Date()}
                  calendarClassName="border-0 shadow-none"
                  dayClassName={(date) => {
                    const today = new Date()
                    const isToday = date.toDateString() === today.toDateString()
                    const isSelected = value && date.toDateString() === value.toDateString()
                    
                    if (isSelected) {
                      return 'bg-blue-500 text-white hover:bg-blue-600'
                    }
                    if (isToday) {
                      return 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }
                    return 'hover:bg-gray-100'
                  }}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

export default DatePicker